import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { ClockCounterClockwise, ArrowCounterClockwise } from "@phosphor-icons/react";

const LABELS = { registered_functions: "Registered functions", impact_ratings: "Impact ratings", no_cip_assets: "No CIP assets",
  regional_entities: "Regions", facilities: "Facilities", smes: "SMEs", glossary_terms: "Glossary terms", name: "Name",
  acronym: "Acronym", ncr_id: "NCR ID", annual_interpretation: "Annual interpretation", compliance_period_start: "Compliance period start",
  compliance_period_end: "Compliance period end", ira_profile: "IRA profile" };
const label = (k) => LABELS[k] || k.replace(/_/g, " ");

// Restorable history of Entity Profile edits (each entry = the profile as it was BEFORE that change).
export const ProfileHistory = ({ refreshKey, onRestored }) => {
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/entity-profile/history", { __silent403: true }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refreshKey]);

  const restore = async () => {
    try {
      const { data } = await api.post(`/entity-profile/history/${confirm.id}/restore`);
      toast.success("Entity Profile restored");
      setConfirm(null);
      onRestored(data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return null;
  return (
    <div className="border border-border bg-card" data-testid="profile-history">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <ClockCounterClockwise size={16} />
        <span className="font-semibold text-sm">Change history</span>
        <span className="text-xs text-muted-foreground">— restore the profile as it was before any change (last 50)</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground" data-testid="profile-history-empty">No changes recorded yet.</div>
      ) : (
        <div className="divide-y divide-border max-h-72 overflow-y-auto">
          {rows.map((h) => (
            <div key={h.id} className="flex items-start gap-3 px-4 py-2 text-xs" data-testid={`profile-history-row-${h.id}`}>
              <div className="flex-1">
                <div><b>{new Date(h.at).toLocaleString()}</b> · {h.by}{h.action === "before_restore" ? " · before a restore" : ""}</div>
                <div className="text-muted-foreground">
                  {h.changed_fields?.length ? `Changed: ${h.changed_fields.map(label).join(", ")}` : "Snapshot"}
                  {" · Functions then: "}{(h.snapshot?.registered_functions || []).join(", ") || "none"}
                </div>
              </div>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" data-testid={`profile-history-restore-${h.id}`}
                onClick={() => setConfirm(h)}>
                <ArrowCounterClockwise size={12} className="mr-1" />Restore this version
              </Button>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this Entity Profile version?</AlertDialogTitle>
            <AlertDialogDescription>
              The profile goes back to how it was before the change on {confirm && new Date(confirm.at).toLocaleString()}.
              The current version is saved to history first, so this can be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="profile-history-confirm-restore" className="rounded-none" onClick={restore}>Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
